import cookies from 'js-cookie';
import {
  DEFAULT_ESC_POS_FEED_LINES_BEFORE_CUT,
  generateTicketESCPOS,
  generateTicketPreviewText,
  getEscPosPaperLayout,
  type EscPosPaperWidth,
  type GenerateEscPosTicketParams,
  type TicketCompanyTaxInfo,
  type TicketImageAsset,
  type TicketLabels,
} from '@washday/utilities';
import type { IOrder } from '../interfaces/Order';
import type {
  ILabelTicketStructure,
  IStore,
  ITicketForLaundryStructure,
  ITicketStructure,
  TicketPrintFormat,
} from '../interfaces/Store';
import RegimenFiscalEnum, { RegimenFiscalType } from '../enum/RegimenFiscalEnum';
import type { PrintDataItem } from './qz-print';

type FrontendTicketStructure =
  | ITicketStructure
  | ITicketForLaundryStructure
  | ILabelTicketStructure
  | null
  | undefined;
type FrontendCompanyTaxInfo = {
  legal_name?: string;
  tax_system?: string;
};
type TicketTranslator = (key: string) => string;
type TicketStructureWithPrintFormat = { ticketPrintFormat?: unknown } | null | undefined;

type GenerateEscPosTicketForFrontendParams = {
  order: IOrder;
  storeSettings?: IStore | null;
  ticketStructure?: FrontendTicketStructure;
  companyTaxInfo?: FrontendCompanyTaxInfo;
  companylogo?: string;
  t: TicketTranslator;
  paperWidth: EscPosPaperWidth;
};
type EscPosOrderParam = GenerateEscPosTicketParams['order'];
type EscPosStoreParam = GenerateEscPosTicketParams['store'];
type EscPosTicketStructureParam = GenerateEscPosTicketParams['ticketStructure'];

const IMAGE_DATA_URL_PREFIX_RE = /^data:image\/[a-zA-Z0-9.+-]+;base64,/i;
const REMOTE_IMAGE_RE = /^(https?:)?\/\//i;
const RAW_BASE64_RE = /^[A-Za-z0-9+/=\s]+$/;
const escPosLogoAssetCache = new Map<string, Promise<TicketImageAsset | undefined>>();

export function resolveTicketPrintFormat(
  ticketStructure?: TicketStructureWithPrintFormat,
): TicketPrintFormat {
  // Backend now defaults every ticket structure to ESC/POS. Frontend keeps an
  // explicit HTML override only when the persisted structure asks for it.
  return ticketStructure?.ticketPrintFormat === 'html' ? 'html' : 'escpos';
}

export function shouldUseEscPosTicketFormat(
  ticketStructure?: TicketStructureWithPrintFormat,
): boolean {
  return resolveTicketPrintFormat(ticketStructure) === 'escpos';
}

export function resolveEscPosPaperWidth(printerPaperSize?: string | null): EscPosPaperWidth {
  return printerPaperSize === '80mm' ? 80 : 58;
}

export function readEscPosPaperWidth(): EscPosPaperWidth {
  const printerPaperSize =
    cookies.get('printerPaperSize') || localStorage.getItem('printerPaperSize');
  return resolveEscPosPaperWidth(printerPaperSize);
}

export function createEscPosQzPrintData(bytes: Uint8Array): PrintDataItem {
  return {
    type: 'raw',
    format: 'command',
    flavor: 'hex',
    data: bytes,
  };
}

function buildEscPosLabels(
  t: TicketTranslator,
  ticketStructure?: FrontendTicketStructure,
): TicketLabels {
  return {
    minimumChargeLabel: t('NewOrder:minimumChargeLabel'),
    taxIdLabel: ticketStructure?.tax_id_label || 'RFC',
    taxesIncludedLabel: t('FieldName:taxesIncluded'),
    paymentMethodLabels: {
      cash: t('FieldName:paymentMethods.cash'),
      card: t('FieldName:paymentMethods.card'),
      transfer: t('FieldName:paymentMethods.transfer'),
      mercadopago: t('FieldName:paymentMethods.mercadopago'),
    },
  };
}

function resolveCompanyTaxInfo(
  companyTaxInfo?: FrontendCompanyTaxInfo,
): TicketCompanyTaxInfo | undefined {
  if (!companyTaxInfo) return undefined;

  const taxSystem = companyTaxInfo.tax_system;

  return {
    legal_name: companyTaxInfo.legal_name,
    tax_system: taxSystem,
    tax_system_label: taxSystem
      ? RegimenFiscalEnum[taxSystem as RegimenFiscalType]
      : undefined,
  };
}

function shouldRenderLogo(
  ticketStructure?: FrontendTicketStructure,
  storeSettings?: IStore | null,
): boolean {
  if (typeof ticketStructure?.printLogoOnTicket === 'boolean') {
    return Boolean(ticketStructure.printLogoOnTicket);
  }

  // TODO: WE WANT TO STOP USING PRINTINC CONFIG PRINTLOGOONTICKET AND ALWAYS USE VALUE FROM TICKETSTRUCTURES (Customer, laundry and label ticket structures has this prop)
  return Boolean(storeSettings?.printingConfig?.printLogoOnTicket);
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function fetchImageAsDataUrl(imageUrl: string): Promise<string | undefined> {
  try {
    const response = await fetch(imageUrl, { mode: 'cors', cache: 'no-store' });
    if (!response.ok) return undefined;
    const blob = await response.blob();
    return await blobToDataUrl(blob);
  } catch (error) {
    console.warn('resolveEscPosLogoAsset fetch failed', error);
    return undefined;
  }
}

async function resolveRenderableImageSource(source: string): Promise<string | undefined> {
  const normalizedSource = source.trim();
  if (!normalizedSource) return undefined;

  if (IMAGE_DATA_URL_PREFIX_RE.test(normalizedSource)) {
    return normalizedSource;
  }

  if (REMOTE_IMAGE_RE.test(normalizedSource) || normalizedSource.startsWith('/')) {
    return await fetchImageAsDataUrl(normalizedSource);
  }

  if (RAW_BASE64_RE.test(normalizedSource)) {
    return `data:image/png;base64,${normalizedSource.replace(/\s+/g, '')}`;
  }

  return undefined;
}

async function loadImage(source: string): Promise<HTMLImageElement> {
  return await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to load ticket image asset'));
    image.src = source;
  });
}

export async function resolveEscPosTicketLogoAsset(
  companylogo: string | undefined,
  paperWidth: EscPosPaperWidth,
): Promise<TicketImageAsset | undefined> {
  if (!companylogo) return undefined;
  const cacheKey = `${paperWidth}:${companylogo}`;
  const cachedAsset = escPosLogoAssetCache.get(cacheKey);
  if (cachedAsset) {
    return await cachedAsset;
  }

  const assetPromise = (async () => {
    const imageSource = await resolveRenderableImageSource(companylogo);
    if (!imageSource) return undefined;

    const image = await loadImage(imageSource);
    const { imageMaxWidth } = getEscPosPaperLayout(paperWidth);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!width || !height) return undefined;

    // Frontend prepares raster assets before delegating to the shared utility.
    // We scale logos down to the effective paper limit so the shared renderer
    // stays portable and receives a safe image contract.
    const targetWidth = Math.min(width, imageMaxWidth);
    const targetHeight = Math.max(1, Math.round(height * (targetWidth / width)));
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to prepare ticket logo image');
    }

    context.drawImage(image, 0, 0, targetWidth, targetHeight);
    const rgba = context.getImageData(0, 0, targetWidth, targetHeight);

    return {
      kind: 'rgba' as const,
      width: targetWidth,
      height: targetHeight,
      data: new Uint8Array(rgba.data),
    };
  })();

  escPosLogoAssetCache.set(cacheKey, assetPromise);

  try {
    return await assetPromise;
  } catch (error) {
    escPosLogoAssetCache.delete(cacheKey);
    throw error;
  }
}

async function buildEscPosTicketParamsForFrontend({
  order,
  storeSettings,
  ticketStructure,
  companyTaxInfo,
  companylogo,
  t,
  paperWidth,
}: GenerateEscPosTicketForFrontendParams): Promise<GenerateEscPosTicketParams> {
  const logo =
    shouldRenderLogo(ticketStructure, storeSettings) && companylogo
      ? await resolveEscPosTicketLogoAsset(companylogo, paperWidth)
      : undefined;

  // HTML still interprets some settings like pixel sizes directly in frontend.
  // ESC/POS must not do that here: the shared utility owns the thermal mapping
  // for options such as customerNameFontSize, orderFolioFontSizePreset, and
  // ticketLineSpacingPreset.
  return {
    order: order as unknown as EscPosOrderParam,
    store: (storeSettings || undefined) as EscPosStoreParam,
    ticketStructure: (ticketStructure || undefined) as EscPosTicketStructureParam,
    companyTaxInfo: resolveCompanyTaxInfo(companyTaxInfo),
    labels: buildEscPosLabels(t, ticketStructure),
    logo,
    paperWidth,
    timeZone: storeSettings?.timezone,
    preferNativeQr: true,
    includeCut: true,
    feedLinesBeforeCut: DEFAULT_ESC_POS_FEED_LINES_BEFORE_CUT,
  };
}

export async function generateEscPosTicketForFrontend(
  params: GenerateEscPosTicketForFrontendParams,
): Promise<Uint8Array> {
  const ticketParams = await buildEscPosTicketParamsForFrontend(params);
  return await generateTicketESCPOS(ticketParams);
}

export async function generateEscPosPreviewTextForFrontend(
  params: GenerateEscPosTicketForFrontendParams,
): Promise<string> {
  const ticketParams = await buildEscPosTicketParamsForFrontend(params);
  return generateTicketPreviewText(ticketParams, {
    paperWidth: ticketParams.paperWidth,
  });
}
